import React from 'react';
import {
  ArrowLeft,
  Globe,
  Copy,
  ExternalLink,
  Activity,
  Clock3,
  ShieldAlert,
  ShieldCheck,
  ShieldX,
  Wrench,
  CheckCircle2,
  AlertTriangle,
  XCircle,
} from 'lucide-react';
import { IncidentHistory } from '../../incidents/components';
import { Footer, UsageMeter } from '../../../shared/components';
import '../../../styles/features/status/EndpointDetailView.css';

function getEndpointState(endpoint) {
  const raw = String(endpoint?.status || '').toLowerCase();

  if (raw === 'up' || raw === 'operational') {
    return {
      key: 'up',
      label: 'All systems operational',
      icon: <CheckCircle2 size={16} />,
    };
  }

  if (raw === 'degraded') {
    return {
      key: 'degraded',
      label: 'Performance degraded',
      icon: <AlertTriangle size={16} />,
    };
  }

  if (raw === 'maintenance') {
    return {
      key: 'maintenance',
      label: 'Under maintenance',
      icon: <Wrench size={16} />,
    };
  }

  if (raw === 'down') {
    return {
      key: 'down',
      label: 'Service disruption',
      icon: <XCircle size={16} />,
    };
  }

  return {
    key: 'unknown',
    label: 'Status unknown',
    icon: <Activity size={16} />,
  };
}

function formatResponse(ms) {
  if (ms == null || Number.isNaN(Number(ms))) return '-';
  return `${Math.round(Number(ms))}ms`;
}

function lossClass(loss) {
  if (loss == null || Number.isNaN(Number(loss))) return '';
  if (Number(loss) === 0) return 'kpi-box_value--ok';
  return Number(loss) < 5 ? 'kpi-box_value--warning' : 'kpi-box_value--danger';
}

const CHECK_LABELS = { http: 'HTTP checks', tcp: 'TCP checks', ping: 'Ping checks' };

function MetricsCard({ title, subtitle, items }) {
  return (
    <section className="card section-card">
      <h3 className="section-title">{title}</h3>
      {subtitle && <p className="section-subtitle">{subtitle}</p>}
      <div className="kpi-grid">
        {items.map((item) => (
          <div className="kpi-box" key={item.label}>
            <span className={`kpi-box_value ${item.className || ''}`}>{item.value ?? '-'}</span>
            <span className="kpi-box_label">{item.label}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function formatPct(v) {
  if (v == null || Number.isNaN(Number(v))) return '-';
  return `${Number(v).toFixed(2)}%`;
}

function renderUrl(endpoint) {
  return endpoint?.url || endpoint?.domain || endpoint?.target || 'No URL';
}

function formatExpiryDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function getSslInfo(ssl) {
  if (!ssl) return null;

  const status = ssl.status || 'ok';
  const days = ssl.days_remaining;
  const expiresLabel = formatExpiryDate(ssl.expires_at);
  const dayLabel = days != null ? `${days} day${days === 1 ? '' : 's'}` : null;

  if (status === 'expired') {
    return { key: 'bad', icon: <ShieldX size={14} />, text: 'Certificate expired' };
  }

  if (status === 'error') {
    return { key: 'bad', icon: <ShieldX size={14} />, text: 'Certificate invalid' };
  }

  if (status === 'warning') {
    const base = dayLabel ? `Certificate expiring in ${dayLabel}` : 'Certificate expiring soon';
    return {
      key: 'warning',
      icon: <ShieldAlert size={14} />,
      text: expiresLabel ? `${base} · expires ${expiresLabel}` : base,
    };
  }

  return {
    key: 'ok',
    icon: <ShieldCheck size={14} />,
    text: dayLabel ? `Certificate valid · expires in ${dayLabel}` : 'Certificate valid',
  };
}

function copyToClipboard(text) {
  if (!text) return;
  navigator.clipboard?.writeText(text).catch(() => {});
}

export default function EndpointDetailView({
  siteTitle = 'Status Page',
  logoUrl = '',
  selectedEndpoint,
  endpointIncidents = [],
  endpointMaintenances = [],
  checkStats = null,
  pingStats = null,
  uptime = {},
  recentEvents = [],
  responseChart = null,
  uptimeChart = null,
  historyBar = null,
  themeToggle = null,
  onBack,
}) {
  const state = getEndpointState(selectedEndpoint);
  const hasIncidents = endpointIncidents.some((incident) => !incident.resolved_at);
  const hasMaintenance = endpointMaintenances.length > 0;
  const url = renderUrl(selectedEndpoint);
  const sslInfo = getSslInfo(selectedEndpoint?.ssl);

  return (
    <div className="app status-page endpoint-page">
      <div className="status-page-content">
        <div className="status-page-header card">
          <div className="status-page-header__title header-brand">
            {logoUrl && (
              <img className="header-brand__logo" src={logoUrl} alt={siteTitle} />
            )}
            <h1>{siteTitle}</h1>
          </div>

          <div className="status-page-header__actions">
            {themeToggle}
            <button
              className="back-to-services-btn"
              onClick={onBack}
              type="button"
            >
              <ArrowLeft size={16} />
              <span>Back to All Services</span>
            </button>
          </div>
        </div>

        <section className={`card endpoint-hero endpoint-hero--${state.key}`}>
          <div className={`endpoint-hero__accent endpoint-hero__accent--${state.key}`} />

          <div className="endpoint-hero__content">
            <div className="endpoint-hero__top">
              <div className="endpoint-hero__identity">
                <h2 className="endpoint-hero__name">
                  {selectedEndpoint?.name || 'Unnamed Service'}
                </h2>

                <div className="endpoint-hero__url-row">
                  {selectedEndpoint?.url ? (
                    <a
                      href={selectedEndpoint.url}
                      target="_blank"
                      rel="noreferrer"
                      className="endpoint-hero__url"
                    >
                      <Globe size={14} />
                      <span>{url}</span>
                      <ExternalLink size={12} className="url-external-icon" />
                    </a>
                  ) : (
                    <span className="endpoint-hero__url endpoint-hero__url--plain">
                      <Globe size={14} />
                      <span>{url}</span>
                    </span>
                  )}

                  <button
                    className="copy-btn"
                    type="button"
                    onClick={() => copyToClipboard(url)}
                    title="Copy URL"
                  >
                    <Copy size={14} />
                  </button>
                </div>
              </div>

              <div className="endpoint-hero__side">
                <div className={`status-callout status-callout--${state.key}`}>
                  <span className="status-callout__icon">{state.icon}</span>
                  <span className="status-callout__text">{state.label}</span>
                </div>

                <div className="endpoint-hero__badges">
                  {hasIncidents && (
                    <span className="badge-incident">
                      <ShieldAlert size={12} />
                      Affected by incident
                    </span>
                  )}

                  {hasMaintenance && (
                    <span className="badge-maintenance">
                      <Wrench size={12} />
                      Maintenance
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="endpoint-meta-chips">
              {selectedEndpoint?.type && (
                <span className="meta-chip">
                  <Activity size={12} />
                  {String(selectedEndpoint.type).toUpperCase()}
                </span>
              )}

              {selectedEndpoint?.checkInterval && (
                <span className="meta-chip">
                  <Clock3 size={12} />
                  Every {selectedEndpoint.checkInterval}s
                </span>
              )}

              {selectedEndpoint?.timeout && (
                <span className="meta-chip">
                  <Clock3 size={12} />
                  Timeout {selectedEndpoint.timeout}s
                </span>
              )}
            </div>

            {sslInfo && (
              <div className={`ssl-status-row ssl-status-row--${sslInfo.key}`}>
                <span className="ssl-status-row__icon">{sslInfo.icon}</span>
                <span className="ssl-status-row__text">{sslInfo.text}</span>
              </div>
            )}

            <div className="endpoint-glance-grid">
              <div className="glance-card">
                <span className="glance-label">Response</span>
                <strong className="glance-value">
                  {/* The latest check's own timing, not the ping average. */}
                  {formatResponse(selectedEndpoint?.responseTime ?? checkStats?.avg_rt)}
                </strong>
              </div>

              <div className="glance-card">
                <span className="glance-label">30D Uptime</span>
                <strong className="glance-value">
                  {formatPct(uptime?.percentage ?? selectedEndpoint?.uptime30d)}
                </strong>
              </div>

              <div className="glance-card">
                <span className="glance-label">Last Check</span>
                <strong className="glance-value">
                  {selectedEndpoint?.lastCheckFormatted ||
                    selectedEndpoint?.lastChecked ||
                    '-'}
                </strong>
              </div>

              <div className="glance-card">
                <span className="glance-label">Checks</span>
                <strong className="glance-value">
                  {uptime?.totalChecks ?? selectedEndpoint?.totalChecks ?? '-'}
                </strong>
              </div>
            </div>
          </div>
        </section>

        <section className="card section-card endpoint-history-full">
          {historyBar || (
            <div className="endpoint-empty-state">No history data.</div>
          )}
        </section>

        <div className="endpoint-dashboard endpoint-dashboard--balanced">
          <aside className="endpoint-dashboard_sidebar">
            {selectedEndpoint?.usage && (
              <section className="card section-card">
                <h3 className="section-title">Storage</h3>
                <UsageMeter usage={selectedEndpoint.usage} />
              </section>
            )}

            {checkStats && (
              <MetricsCard
                title="Response"
                subtitle={`${CHECK_LABELS[selectedEndpoint?.type] || 'Checks'} · last 24h`}
                items={[
                  { label: 'Avg', value: formatResponse(checkStats.avg_rt) },
                  { label: 'Min', value: formatResponse(checkStats.min_rt) },
                  { label: 'Max', value: formatResponse(checkStats.max_rt) },
                  { label: 'Jitter', value: formatResponse(checkStats.jitter) },
                  { label: 'Failed', value: `${checkStats.failure_rate}%`, className: lossClass(checkStats.failure_rate) },
                  { label: 'Checks', value: checkStats.total },
                ]}
              />
            )}

            {pingStats && (
              <MetricsCard
                title="Ping"
                subtitle="ICMP · last 24h"
                items={[
                  { label: 'Avg', value: formatResponse(pingStats.avg_rt) },
                  { label: 'Min', value: formatResponse(pingStats.min_rt) },
                  { label: 'Max', value: formatResponse(pingStats.max_rt) },
                  { label: 'Jitter', value: formatResponse(pingStats.jitter) },
                  { label: 'Loss', value: `${pingStats.packet_loss}%`, className: lossClass(pingStats.packet_loss) },
                  { label: 'Pings', value: pingStats.total },
                ]}
              />
            )}

            <section className="card section-card">
              <h3 className="section-title">30 Day Uptime</h3>
              <div className="endpoint-sidebar-chart">
                {uptimeChart || (
                  <div className="endpoint-empty-state">No uptime data.</div>
                )}
              </div>
            </section>
          </aside>

          <main className="endpoint-dashboard_main">
            <section className="card section-card chart-section chart-section--large">
              {responseChart || (
                <div className="endpoint-empty-state">No response chart data.</div>
              )}
            </section>

            <section className="card section-card">
              <h3 className="section-title">Recent Events</h3>

              <div className="events-timeline">
                {recentEvents?.length ? (
                  recentEvents.map((event, idx) => (
                    <div className="event-item" key={`${event.label || 'event'}-${idx}`}>
                      <span className={`event-dot event-dot--${event.status || 'unknown'}`} />
                      <div className="event-content">
                        <span className="event-status">{event.label}</span>
                        <span className="event-time">{event.time}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="endpoint-empty-state">No recent events.</div>
                )}
              </div>
            </section>
          </main>
        </div>

        {endpointIncidents.length > 0 && (
          <section className="card section-card endpoint-incidents-full">
            <div className="section-title-row">
              <h3 className="section-title">Service Incidents</h3>
            </div>
            <IncidentHistory incidents={endpointIncidents} />
          </section>
        )}

        <Footer />
      </div>
    </div>
  );
}
